import { Injectable, inject, signal } from '@angular/core';

import {

  Firestore,

  collection,

  doc,

  getDocs,

  setDoc,

  query,

  orderBy,

  serverTimestamp,

} from '@angular/fire/firestore';

import { MasterLocation } from '../models/location.model';
import { formBlockOptionsFromMasters, userHasUnrestrictedBlockAccess } from '../utils/block-access.utils';
import { OPERATOR_LOCATION_BLOCKS } from '../constants/operator-master-data';
import { UserRole } from '../models/user.model';



const COLLECTION = 'master_locations';



/** Seed when Firestore collection is empty — admin can add more later. */

const DEFAULT_LOCATIONS: Omit<MasterLocation, 'id' | 'createdAt'>[] = [

  {

    name: 'Dhamnagar Block',

    type: 'BLOCK',

    active: true,

    aliases: ['Dhamnagar', 'Dhamanagar', 'Dhamnagar Block'],

  },

  {

    name: 'Dhamnagar NAC',

    type: 'NAC',

    active: true,

    aliases: ['Dhamnagar NAC'],

  },

  {

    name: 'Dhusuri NAC',

    type: 'NAC',

    active: true,

    aliases: ['Dhusuri NAC'],

  },

  {

    name: 'Tihidi Block',

    type: 'BLOCK',

    active: true,

    aliases: ['Tihidi', 'Tihidi Block'],

  },

  {

    name: 'Tihidi NAC',

    type: 'NAC',

    active: true,

    aliases: ['Tihidi NAC'],

  },

];



function locationDocId(name: string): string {

  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

}



function mapDoc(id: string, data: Record<string, unknown>): MasterLocation {

  return {

    id,

    name: String(data['name'] ?? ''),

    type: (data['type'] as MasterLocation['type']) ?? 'BLOCK',

    active: data['active'] !== false,

    aliases: Array.isArray(data['aliases']) ? (data['aliases'] as string[]) : undefined,

    createdAt: data['createdAt'],

    updatedAt: data['updatedAt'],

  };

}



@Injectable({ providedIn: 'root' })

export class LocationMasterService {

  private readonly firestore = inject(Firestore);

  private readonly col = collection(this.firestore, COLLECTION);



  readonly locations = signal<MasterLocation[]>(this.buildFallbackLocations());

  readonly isLoaded = signal(false);

  private loadPromise: Promise<MasterLocation[]> | null = null;



  /** Active locations — loads from Firestore, falls back to defaults if needed. */

  async ensureLoaded(): Promise<MasterLocation[]> {

    if (this.isLoaded()) return this.activeLocations();

    if (!this.loadPromise) {

      this.loadPromise = this.loadAndSeed();

    }

    return this.loadPromise;

  }



  activeLocations(): MasterLocation[] {

    return this.locations()

      .filter(l => l.name && l.active !== false)

      .sort((a, b) => a.name.localeCompare(b.name));

  }

  /** Block dropdown options for new-project forms. */
  formBlockOptions(
    blockAccess?: string[] | null,
    role?: UserRole | string,
  ): string[] {
    return formBlockOptionsFromMasters(
      this.activeLocations(),
      blockAccess,
      userHasUnrestrictedBlockAccess(role),
      OPERATOR_LOCATION_BLOCKS,
    );
  }



  private buildFallbackLocations(): MasterLocation[] {

    return DEFAULT_LOCATIONS.map(loc => ({

      id: locationDocId(loc.name),

      ...loc,

    })).sort((a, b) => a.name.localeCompare(b.name));

  }



  private async loadAndSeed(): Promise<MasterLocation[]> {

    try {

      const snap = await getDocs(query(this.col, orderBy('name', 'asc')));

      if (snap.empty) {

        try {

          await Promise.all(

            DEFAULT_LOCATIONS.map(loc =>

              setDoc(doc(this.col, locationDocId(loc.name)), {

                ...loc,

                createdAt: serverTimestamp(),

              })

            )

          );

          const seeded = await getDocs(query(this.col, orderBy('name', 'asc')));

          this.locations.set(seeded.docs.map(d => mapDoc(d.id, d.data() as Record<string, unknown>)));

        } catch (seedErr) {

          console.warn('[LocationMaster] Seed failed — using built-in defaults.', seedErr);

          this.locations.set(this.buildFallbackLocations());

        }

      } else {

        this.locations.set(snap.docs.map(d => mapDoc(d.id, d.data() as Record<string, unknown>)));

      }

    } catch (readErr) {

      console.warn('[LocationMaster] Read failed — using built-in defaults.', readErr);

      this.locations.set(this.buildFallbackLocations());

    }



    this.isLoaded.set(true);

    return this.activeLocations();

  }

}


